import {
  latLongToECEF,
} from "../../utils/googleMaps";
import ThreeDScene from "../ThreeDScene";
import { BaseThreeDFactory } from "./BaseThreeDFactory";
import * as THREE from "three";
import { deleteBlob, downloadBlob, loadModel, uploadBlob } from "../../api/connection";
import { Site, createSite } from "../../models/Site";
import { toPoint } from "mgrs";
import { showSnackbar } from "../UniversalDialog";
import { bufferToGrid, gridToBuffer } from "../../utils/ioUtils";
import { MapWall, Vec2, Vec3 } from "../../models/SyntheticMap";
import { isPointInsidePolygon, normalizeAngleToPi } from "../../utils/spatial";
import { smoothPolygon } from "../../utils/rdp";
import {
  TilesRenderer
} from "3d-tiles-renderer";
import {
  GoogleCloudAuthPlugin,
  UpdateOnChangePlugin,
  TileCompressionPlugin,
  UnloadTilesPlugin,
  GLTFExtensionsPlugin,
  // TopoLinesPlugin
} from '3d-tiles-renderer/src/plugins';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

//import { GoogleCloudAuth } from "../../utils/GoogleCloudAuth";
// import { GoogleCloudAuthPlugin } from "3d-tiles-renderer/plugins";

const RIDGE_THRESHOLD = 0.3; // meters, threshold to consider a point as a ridge

export class Tile3DFactory extends BaseThreeDFactory {
  private debounceTimeout: any;
  // private terrainGroup!: THREE.Group;
  public site!: Site;
  private floorWalls: MapWall[] = [];
  // private abortController: AbortController | null = null;
  private remainingTilesToLoad: string[] = [];
  async load(): Promise<void> {
    (this.scene as ThreeDScene).tile3DFactory = this;
  }

  public defineFloor(position: THREE.Vector3) {
    const ridgePointGrid = this.getRidgePointGrid();
    const startX = Math.floor((position.x + this.wsManagement.mapService.map.settings.floorSize / 2) / this.elevationGridResolution);
    const startY = Math.floor((position.y + this.wsManagement.mapService.map.settings.floorSize / 2) / this.elevationGridResolution);
    if (startX < 0 || startY < 0 || startX >= this.elevationGrid.length || startY >= this.elevationGrid[0].length) {
      console.error("Position out of bounds for elevation grid:", position);
      return;
    }
    const rows = ridgePointGrid.length;
    const cols = ridgePointGrid[0].length;
    const visitedNonRidge: Set<string> = new Set(); // Track visited non-ridge points
    const connectedNonRidgePoints: [number, number][] = []; // Store connected non-ridge points    
    // DFS to find all connected non-ridge points
    function dfsNonRidge(x: number, y: number): void {
      if (x < 0 || x >= rows || y < 0 || y >= cols || visitedNonRidge.has(`${x},${y}`) || ridgePointGrid[x][y] === 1) {
        return;
      }

      visitedNonRidge.add(`${x},${y}`);
      connectedNonRidgePoints.push([x, y]);

      const directions: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dx, dy] of directions) {
        dfsNonRidge(x + dx, y + dy);
      }
    }

    // DFS to find the size of a ridge area
    function getRidgeAreaSize(x: number, y: number, visitedRidge: Set<string>): number {
      if (x < 0 || x >= rows || y < 0 || y >= cols || visitedRidge.has(`${x},${y}`) || ridgePointGrid[x][y] === 0) {
        return 0;
      }
      visitedRidge.add(`${x},${y}`);
      let size = 1;
      const directions: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dx, dy] of directions) {
        size += getRidgeAreaSize(x + dx, y + dy, visitedRidge);
      }
      return size;
    }

    // Step 1: Find all connected non-ridge points
    dfsNonRidge(startX, startY);

    // Step 2: Filter non-ridge points adjacent to ridge areas
    const filteredPoints: [number, number][] = [];
    const visitedRidgeGlobal: Set<string> = new Set(); // Track all visited ridge points globally
    const MIN_RIDGE_SIZE = 5; // Minimum size of ridge area to consider
    const upperLeft: Vec2 = { x: Number.MAX_SAFE_INTEGER, y: Number.MAX_SAFE_INTEGER };
    const lowerRight: Vec2 = { x: 0, y: 0 };
    for (const [x, y] of connectedNonRidgePoints) {
      const directions: [number, number][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      for (const [dx, dy] of directions) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx >= 0 && nx < rows && ny >= 0 && ny < cols && ridgePointGrid[nx][ny] === 1 && !visitedRidgeGlobal.has(`${nx},${ny}`)) {
          const ridgeSize = getRidgeAreaSize(nx, ny, new Set<string>());
          if (ridgeSize > MIN_RIDGE_SIZE) {
            upperLeft.x = Math.min(upperLeft.x, nx);
            upperLeft.y = Math.min(upperLeft.y, ny);
            lowerRight.x = Math.max(lowerRight.x, nx);
            lowerRight.y = Math.max(lowerRight.y, ny);
            filteredPoints.push([x, y]);
            break; // Include the point if it touches a large ridge area
          }
        }
      }
    }
    const floorAreaSize: Vec2 = {
      x: (lowerRight.x - upperLeft.x + 1),
      y: (lowerRight.y - upperLeft.y + 1),
    };
    // create an array of zeros with the size of the floor area
    const floorArea: number[][] = Array.from({ length: floorAreaSize.x }, () => Array(floorAreaSize.y).fill(0));
    for (const [x, y] of filteredPoints) {
      // populate the floor area with 100 for each filtered point
      const adjustedX = x - upperLeft.x;
      const adjustedY = y - upperLeft.y;
      if (adjustedX >= 0 && adjustedX < floorAreaSize.x && adjustedY >= 0 && adjustedY < floorAreaSize.y) {
        floorArea[adjustedX][adjustedY] = 100; // Mark as part of the floor area
      }
    }
    const startPoint = [filteredPoints[0][0] - upperLeft.x, filteredPoints[0][1] - upperLeft.y] as [number, number];
    if (!startPoint) {
      console.warn("No valid points found for floor area.");
      return;
    }
    const visitedSortedPoints: Set<string> = new Set();
    visitedSortedPoints.add(`${startPoint[0]},${startPoint[1]}`);
    const sortedPoints: [number, number][] = [startPoint];
    const dist = 2;
    const distanceEstimator = (a: [number, number], b: [number, number]) => {
      return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2);
    }
    const maxIterations = filteredPoints.length * 2; // Limit iterations to avoid infinite loops
    let iterations = 0;
    while (iterations < maxIterations) {
      iterations++;
      const lastPoint = sortedPoints[sortedPoints.length - 1];
      // find nearest in floor area grid that is not visited with 2 cells
      let nearestPoint: [number, number] | null = null;
      let nearestDistance = Infinity;
      const startX = Math.max(0, lastPoint[0] - dist);
      const endX = Math.min(floorAreaSize.x - 1, lastPoint[0] + dist);
      const startY = Math.max(0, lastPoint[1] - dist);
      const endY = Math.min(floorAreaSize.y - 1, lastPoint[1] + dist);
      for (let x = startX; x <= endX; x++) {
        for (let y = startY; y <= endY; y++) {
          if (floorArea[x][y] === 100 && !visitedSortedPoints.has(`${x},${y}`)) {
            const distance = distanceEstimator([x, y], lastPoint);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearestPoint = [x, y];
            }
          }
        }
      }
      if (!nearestPoint) {
        break; // No more points to visit
      } else {
        sortedPoints.push(nearestPoint);
        visitedSortedPoints.add(`${nearestPoint[0]},${nearestPoint[1]}`);
      }
      const distanceToStart = distanceEstimator(sortedPoints[0], nearestPoint);
      if (sortedPoints.length > 5 && distanceToStart <= dist) {
        // If the nearest point is close to the start point, we can stop
        break;
      }
    }

    // convert sorted to big grid coordinates
    const sortedGridPoints: Vec2[] = sortedPoints.map(([x, y]) => ({
      x: x + upperLeft.x,
      y: y + upperLeft.y,
    }));


    // convert filtered points to map coordinates
    const map = this.wsManagement.mapService.map;
    const side = map.settings.floorSize / 2;
    const mapPoints: Vec2[] = sortedGridPoints.map(point => ({
      x: (point.x - side) / this.elevationGridResolution,
      y: (point.y - side) / this.elevationGridResolution,
    }));
    // find index of a point closest to position
    const closestIndex = mapPoints.reduce((closest, point, index) => {
      const distance = Math.sqrt(
        (point.x - position.x) ** 2 + (point.y - position.y) ** 2
      );
      return distance < closest.distance ? { index, distance } : closest;
    }, { index: -1, distance: Infinity }).index;
    // rotate the map points so the first point is the closest
    const rotatedPoints = mapPoints.slice(closestIndex).concat(mapPoints.slice(0, closestIndex));
    let smoothPoints = smoothPolygon(rotatedPoints, 2, 10);
    const mapWall: MapWall = {
      points: smoothPoints,
      type: "textured",
      floor: 0,
      definesFloor: true,
    }
    this.wsManagement.mapService.map.walls.push(mapWall);
    this.floorWalls.push(mapWall);

  }


  public supportsSelection(obj: THREE.Object3D): boolean {
    return false;
  }

  public getSceneObjects(): THREE.Object3D<THREE.Object3DEventMap>[] {
    return [];
  }

  public castShadow() {
    return true;
  }

  public receiveShadow() {
    return false;
  }


  public debouncedMapLoad() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(async () => {
      await this.onMapLoad();
      this.debounceTimeout = undefined;
    }, 1000);
  }

  // public clearTerrainGroup() {
  //   if (this.terrainGroup) {
  //     this.terrainGroup.traverse((child) => {
  //       if (child instanceof THREE.Mesh) {
  //         const mesh = child as THREE.Mesh;
  //         mesh.geometry.dispose();
  //       }
  //     });
  //     this.scene.remove(this.terrainGroup);
  //   }
  // }

  public drawRaycastPlane(limit: THREE.Scene): THREE.Vector3[] {
    const center = new THREE.Vector3(0, 0, 6); // Center point
    const raycaster = new THREE.Raycaster();
    const rayMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
    const intersectionMaterial = new THREE.LineBasicMaterial({
      color: 0xff00ff,
      linewidth: 5,
    });

    // Collect all line objects from the limit scene
    const limitLines = limit.children.filter(
      (child): child is THREE.Line => child instanceof THREE.Line
    );

    // Number of points to interpolate per line segment
    const pointsPerSegment = 2;

    // Extract and interpolate points along limit lines
    const limitPoints: THREE.Vector3[] = [];
    limitLines.forEach((line) => {
      const positions = line.geometry.attributes.position.array;
      for (let i = 0; i < positions.length - 3; i += 3) {
        const start = new THREE.Vector3(positions[i], positions[i + 1], 6);
        const end = new THREE.Vector3(positions[i + 3], positions[i + 4], 6);
        for (let j = 0; j <= pointsPerSegment; j++) {
          const t = j / pointsPerSegment;
          const point = new THREE.Vector3().lerpVectors(start, end, t);
          limitPoints.push(point);
        }
      }
      if (positions.length >= 6) {
        const lastStart = new THREE.Vector3(
          positions[positions.length - 6],
          positions[positions.length - 5],
          6
        );
        const lastEnd = new THREE.Vector3(
          positions[positions.length - 3],
          positions[positions.length - 2],
          6
        );
        for (let j = 0; j <= pointsPerSegment; j++) {
          const t = j / pointsPerSegment;
          const point = new THREE.Vector3().lerpVectors(lastStart, lastEnd, t);
          limitPoints.push(point);
        }
      }
    });

    // Array to store terrain intersection points
    const intersectionPoints: THREE.Vector3[] = [];

    // Cast rays from each interpolated point toward the center
    limitPoints.forEach((origin) => {
      const direction = new THREE.Vector3()
        .subVectors(center, origin)
        .normalize();

      raycaster.set(origin, direction);

      const terrainIntersects = raycaster.intersectObjects(
        this.tilesRenderer!.group!.children,
        true
      );

      let endPoint: THREE.Vector3 | null = null;

      if (terrainIntersects.length > 0) {
        endPoint = terrainIntersects[0].point;
        intersectionPoints.push(endPoint.clone()); // Store intersection point
      }

      if (!endPoint) {
        endPoint = center.clone();
      }

      // Draw the ray
      const geometry = new THREE.BufferGeometry().setFromPoints([
        origin,
        endPoint,
      ]);
      const line = new THREE.Line(geometry, rayMaterial);
      this.scene.add(line);
    });

    // Draw a line connecting all intersection points
    if (intersectionPoints.length > 1) {
      // Need at least 2 points to form a line
      const intersectionGeometry = new THREE.BufferGeometry().setFromPoints(
        intersectionPoints
      );
      const intersectionLine = new THREE.Line(
        intersectionGeometry,
        intersectionMaterial
      );
      this.scene.add(intersectionLine);
    }

    return intersectionPoints;
  }




  public update() {
    if (this.tilesRenderer) {
      this.tilesRenderer.update();
    }
  }

  public tilesRenderer: TilesRenderer | null = null;
  private radius = 0;
  private async renderTiles() {
    const scene = this.scene;
    if (this.tilesRenderer) {
      scene.remove(this.tilesRenderer.group);
      this.tilesRenderer.dispose();
    }
    const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string;

    const tilesUrl = `https://tile.googleapis.com/v1/3dtiles/root.json`;
    this.tilesRenderer = new TilesRenderer(tilesUrl);
    // this.tilesRenderer.errorTarget = 0.1;
    // this.tilesRenderer.lruCache.maxSize = 20000; // Larger value keeps more high-res tiles cached
    // this.tilesRenderer.lruCache.minSize = 15000; // Minimum before unloading starts
    this.tilesRenderer.setCamera(scene.camera);
    this.tilesRenderer.setResolutionFromRenderer(scene.camera, scene.renderer);
    this.tilesRenderer.registerPlugin(new GoogleCloudAuthPlugin({ apiToken: API_KEY }));
    const tiles = this.tilesRenderer;
    tiles.registerPlugin(new TileCompressionPlugin());
    tiles.registerPlugin(new UpdateOnChangePlugin());
    tiles.registerPlugin(new UnloadTilesPlugin({ bytesTarget: 1024 * 1024 * 1024 }));
    // tiles.registerPlugin(new TilesFadePlugin());
    // tiles.registerPlugin(new TopoLinesPlugin({ projection: 'ellipsoid' }));
    tiles.registerPlugin(new GLTFExtensionsPlugin({
      // Note the DRACO compression files need to be supplied via an explicit source.
      // We use unpkg here but in practice should be provided by the application.
      dracoLoader: new DRACOLoader().setDecoderPath('https://unpkg.com/three@0.153.0/examples/jsm/libs/draco/gltf/')
    }));

    scene.add(this.tilesRenderer.group);
    const [lon, lat] = toPoint(this.site.location);
    //const { original } = latLonToVector3(lat, lon);
    const original = latLongToECEF(lat, lon, this.site.elevation);
    this.radius = original.length();
    const normalized = new THREE.Vector3(original.x, original.y, original.z).normalize();
    const elevationOffset = this.wsManagement.mapService.map.settings.elevationOffset || 0;
    this.tilesRenderer.group.position.set(0, 0, -(this.radius + this.site.elevation + elevationOffset));
    // Compute geocentric latitude in radians from z-component
    const geocentric_phi_rad = Math.asin(normalized.z);
    // Compute longitude in radians (atan2 returns -PI to PI)
    const lambda_rad = Math.atan2(normalized.y, normalized.x);
    // Rotation around Y: tilt to align point to +Z
    const rotationY = Math.PI / 2 - geocentric_phi_rad;
    // Rotation around Z: align meridians (adjusted to match your original π - toRad(lon))
    const rotationZ = Math.PI - lambda_rad;
    this.tilesRenderer.group.rotation.y = rotationY;
    this.tilesRenderer.group.rotation.z = rotationZ;
  }
  /*
  private async renderTilesOld(point2: THREE.Vector3) {
    if (!this.remainingTilesToLoad.length) {
      this.handleElevationGrid();
      return;
    }
    const triggerTiles = this.remainingTilesToLoad;
    const tilesToLoad = this.remainingTilesToLoad.splice(0, 10);
    console.log("remaining:", this.remainingTilesToLoad.length);
    await Promise.all(
      tilesToLoad.map(async (tileName) => {
        const data = await downloadBlob("techmage-tiles", tileName, 100 * 24 * 3600);
        if ("error" in data) {
          console.error("Error downloading blob:", data.error);
          return;
        }
        const blob = new Blob([data], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const tileContent = await loadTileContent(url);
        URL.revokeObjectURL(url);
        if (triggerTiles !== this.remainingTilesToLoad) {
          return;
        }
        const group3d = this.terrainGroup.children[0] as THREE.Group;
        const mesh = tileContent!.children[0] as THREE.Mesh;
        mesh.position.sub(point2);
        group3d.add(mesh);
        // Set opacity immediately
        const terrainVisibility = this.wsManagement.mapService.map.settings.terrainVisibility ?? 1;
        mesh.traverse((obj) => {
          if (obj instanceof THREE.Mesh && obj.material) {
            const mat = obj.material;
            const apply = (material: THREE.Material) => {
              material.transparent = true;
              material.opacity = terrainVisibility;
              material.depthWrite = true;
              material.needsUpdate = true;
            };
            // obj.receiveShadow = true; - this doesn't do anything, shadows are not supported for terrain
            if (Array.isArray(mat)) {
              mat.forEach(m => m && apply(m));
            } else {
              apply(mat);
            }
          }
        });
      })
    );
    if (triggerTiles !== this.remainingTilesToLoad) {
      return;
    }
    this.renderTiles(point2);
    // if (!this.remainingTilesToLoad.length) {
    //   this.adjustForMapSiteProperties();
    // }

  }
  */
  async onMapLoad() {
    this.elevationGridResolution = 0;
    const map = this.wsManagement.mapService.map;
    const siteId = map.settings.siteId;
    if (this.tilesRenderer) {
      this.scene.remove(this.tilesRenderer.group);
      this.tilesRenderer.dispose();
    }
    // Exit early if no siteId
    if (!siteId) {
      this.adjustForMapSiteProperties();
      // this.scene.remove(this.tilesRenderer?.group);
      return;
    }

    // Exit early if site hasn't changed and terrain already loaded
    // if (this.site && this.site._id === siteId ) {
    //   this.adjustForMapSiteProperties();
    //   return;
    // }

    // // If site changed or terrain is missing, update terrain
    // if (!this.site || this.site._id !== siteId) {
    //   this.clearTerrainGroup();
    // }

    // this.adjustForMapSiteProperties();

    // if (this.site && this.site._id === siteId && !this.terrainGroup.children.length) {
    //   const latLng = toPoint(this.site.location);
    //   const point = latLongToECEF(latLng[1], latLng[0], this.site.elevation);
    //   const point3D = new THREE.Vector3(point.x, point.z, -point.y);
    //   this.renderTiles(point3D);
    //   return;
    // }

    const site = createSite(siteId);
    await loadModel(site);

    // if (!site.numberOfTiles) return;

    // const tileNames = getTileNames(site);
    // this.remainingTilesToLoad = tileNames;
    this.site = site;
    // const latLng = toPoint(site.location);
    // const point = latLongToECEF(latLng[1], latLng[0], site.elevation);
    // const point2 = new THREE.Vector3(point.x, point.z, -point.y);
    // const group3d = new THREE.Group();

    // const zMatrix = createRotationMatrixToAlignVectorWithZAxis(point2);
    // // apply zMatrix to group3d
    // group3d.applyMatrix4(zMatrix);

    // // const terrainGroup = new THREE.Group();
    // // terrainGroup.add(group3d);
    // // const angle = computeZRotationToAlignLocalNorthWithGlobal(group3d);
    // // terrainGroup.rotateZ(angle);
    // // this.terrainGroup = terrainGroup;
    // // this.scene.add(terrainGroup);

    // this.adjustForMapSiteProperties();
    this.renderTiles().then(() => {
      this.adjustForMapSiteProperties();
    });
  }

  public adjustForMapSiteProperties() {
    const map = this.wsManagement.mapService.map;
    // const mapFloorFactory = this.wsManagement.uiService.components.find((f) => f instanceof MapFloorFactory) as MapFloorFactory;
    const elevationOffset = map.settings.elevationOffset || 0;
    if (this.tilesRenderer) {
      this.tilesRenderer.group.position.set(0, 0, -(this.radius + this.site.elevation + elevationOffset));
    }
    // this.terrainGroup.position.z = -this.site.elevation - elevationOffset;
    // const group3d = this.terrainGroup.children[0] as THREE.Group;
    const terrainVisibility = map.settings.terrainVisibility;
    const isVisible = terrainVisibility > 0.2;
    // group3d.visible = isVisible;
    const group3d = this.tilesRenderer?.group;
    if (!group3d) {
      return;
    }
    group3d.visible = isVisible;
    this.handleElevationGrid();
  }

  private adjustElevationGrid() {
    const map = this.wsManagement.mapService.map;
    const walls = map.walls.filter(wall => wall.definesFloor);
    const wallPoints: Vec2[][] = [];
    for (const wall of walls) {
      const points = wall.points.map(p => ({
        x: (p.x + map.settings.floorSize / 2) / this.elevationGridResolution,
        y: (p.y + map.settings.floorSize / 2) / this.elevationGridResolution,
      }));
      wallPoints.push(points);
    }
    const gateRegions = map.regions.filter(region => region.isGate);
    for (const region of gateRegions) {
      const corners = [
        {
          x: region.pose.x - region.size.x / 2,
          y: region.pose.y - region.size.y / 2,
        },
        {
          x: region.pose.x + region.size.x / 2,
          y: region.pose.y - region.size.y / 2,
        },
        {
          x: region.pose.x + region.size.x / 2,
          y: region.pose.y + region.size.y / 2,
        },
        {
          x: region.pose.x - region.size.x / 2,
          y: region.pose.y + region.size.y / 2,
        },
      ];
      // rotate corners to match region.pose.rotation
      const rotation = normalizeAngleToPi(region.pose.w);
      const cos = Math.cos(rotation);
      const sin = Math.sin(rotation);
      for (let i = 0; i < corners.length; i++) {
        const corner = corners[i];
        const x = corner.x - region.pose.x;
        const y = corner.y - region.pose.y;
        corners[i].x = x * cos - y * sin + region.pose.x;
        corners[i].y = x * sin + y * cos + region.pose.y;
      }

      const points = corners.map(p => ({
        x: (p.x + map.settings.floorSize / 2) / this.elevationGridResolution,
        y: (p.y + map.settings.floorSize / 2) / this.elevationGridResolution,
      }));
      wallPoints.push(points);
    }
    for (let i = 0; i < this.elevationGrid.length; i++) {
      for (let j = 0; j < this.elevationGrid[i].length; j++) {
        const point = { x: i, y: j };
        for (const points of wallPoints) {
          if (isPointInsidePolygon(point, points)) {
            this.elevationGrid[i][j] = 0; // Set elevation to 0 if inside a wall polygon
          }
        }
      }
    }
    // // make entries in the map
    // for (const wall of wallPoints) {
    //   const first = wall[0];
    //   const last = wall[wall.length - 1];
    //   const distance = Math.sqrt((first.x - last.x) ** 2 + (first.y - last.y) ** 2);
    //   if (distance > 3 * this.elevationGridResolution) {
    //     // create a gap in the wall
    //   }
    // }
  }

  private ridgePointGrid: number[][] = [];

  public adjustRaysOnRidgePoints(
    loc: Vec3,
    rotationZ: number,
    rays: number[],
    scanStart: number,
    range: number,
    step: number) {
    if (!this.elevationGridResolution) {
      return false;
    }
    if (this.ridgePointGrid.length === 0) {
      this.ridgePointGrid = this.getRidgePointGrid();
    }
    const side = this.wsManagement.mapService.map.settings.floorSize / 2;
    const x = Math.floor((loc.x + side) / this.elevationGridResolution);
    const y = Math.floor((loc.y + side) / this.elevationGridResolution);
    const start = normalizeAngleToPi(rotationZ + scanStart);
    const linearStep = 1;
    const scaledRange = range / this.elevationGridResolution;
    for (let i = 5; i < rays.length; i++) {
      const angle = normalizeAngleToPi(start + (i - 5) * step);
      const maxDistance = rays[i] / this.elevationGridResolution;
      let distance = 0;
      const dx = Math.cos(angle) * linearStep;
      const dy = Math.sin(angle) * linearStep;
      let currentX = x;
      let currentY = y;
      while (distance < maxDistance && distance < scaledRange) {
        if (currentX < 0 || currentY < 0 || currentX >= this.ridgePointGrid.length || currentY >= this.ridgePointGrid[0].length) {
          break; // Out of bounds
        }
        if (this.ridgePointGrid[currentX][currentY] === 1) {
          // Ridge point found, adjust distance
          rays[i] = Math.min(rays[i], distance * this.elevationGridResolution);
          break;
        }
        distance += linearStep;
        currentX = Math.round(dx + currentX);
        currentY = Math.round(dy + currentY);
      }
    }
  }

  public async handleElevationGrid() {
    // debugger;
    const map = this.wsManagement.mapService.map;
    const blobName = `elevation_grid_${map._id}.json`;
    if (!map.settings.elevationGrid) {
      if (this.elevationGridResolution !== 0) {
        await deleteBlob("techmage-tiles", blobName);
        this.elevationGrid = [];
        this.elevationGridResolution = 0;
      }
      return;
    }
    if (this.elevationGridResolution === 0) {
      const blob = await downloadBlob("techmage-tiles", blobName);
      if (!("error" in blob) && blob.byteLength > 100) {
        console.log("Downloaded elevation grid size", blob.byteLength);
        const arrayBuffer = blob as ArrayBuffer;
        const data = bufferToGrid(arrayBuffer);
        const formatLine = data.pop() as number[];
        this.elevationGridResolution = formatLine[0];
        this.elevationGrid = data;
      } else {
        this.createElevationGrid(1.0);
        const buffer = gridToBuffer([...this.elevationGrid, [this.elevationGridResolution]]);
        console.log("Uploading elevation grid size", buffer.byteLength);
        await uploadBlob("techmage-tiles", blobName, buffer);
      }

      this.adjustElevationGrid();
      this.ridgePointGrid = [];
      this.ridgePoints = this.getRidgePoints();
      // this.createRidgeBoxes();
      this.visualizeElevationGrid(true);
      showSnackbar("Elevation grid created");
      setTimeout(() => this.visualizeElevationGrid(false), 2000);
    }
  }



  public renderMap(ctx: CanvasRenderingContext2D, resolution: number, convertToMapCoordinates: (x: number, y: number) => Vec2): void {
    if (!this.elevationGridResolution) {
      return;
    }

    // const ridgePoints: Vec2[] = this.getRidgePoints();
    ctx.save();
    const pointSize = Math.round(this.elevationGridResolution / resolution);
    const halfPointSize = Math.round(pointSize / 1.0);
    // Disable anti-aliasing to prevent gray edges
    ctx.imageSmoothingEnabled = false;
    ctx.strokeStyle = 'transparent'; // Explicitly set stroke to transparent
    ctx.fillStyle = 'black'; // Set fill color to black
    ctx.lineWidth = 0; // Disable line width    ctx.fillStyle = "black";
    // this.ridgePoints = this.getRidgePoints();
    for (const point of this.ridgePoints) {
      const { x, y } = convertToMapCoordinates(point.x, point.y);
      ctx.fillRect(x - halfPointSize, y - halfPointSize, pointSize * 2, pointSize * 2);
    }
  }

  private getNeighborElevations(x: number, y: number): number[] {
    const neighbors = [
      { dx: -1, dy: 0 }, // left
      { dx: 1, dy: 0 }, // right
      { dx: -1, dy: -1 }, // top left
      { dx: -1, dy: 1 }, // bottom left
      { dx: 1, dy: -1 }, // top right
      { dx: 1, dy: 1 }, // bottom right
      { dx: 0, dy: -1 }, // up
      { dx: 0, dy: 1 }, // down
    ];
    const defaultElevation = this.elevationGrid[x][y];
    return neighbors.map(({ dx, dy }) => {
      const nx = x + dx;
      const ny = y + dy;
      if (nx < 0 || ny < 0 || nx >= this.elevationGrid.length || ny >= this.elevationGrid[0].length) {
        return defaultElevation;
      }
      return this.elevationGrid[nx][ny];
    });
  };

  private getRidgePointGrid() {
    const ridgePointGrid: number[][] = [];
    for (let x = 0; x < this.elevationGrid.length; x++) {
      const row: number[] = [];
      ridgePointGrid.push(row);
      for (let y = 0; y < this.elevationGrid[x].length; y++) {
        const elevation = this.elevationGrid[x][y];
        const neighbors = this.getNeighborElevations(x, y);
        if (neighbors.some(z => elevation - z > RIDGE_THRESHOLD)) {
          row.push(1); // Mark as ridge point
        } else {
          row.push(0); // Not a ridge point
          continue;
        }
      }
    }
    return ridgePointGrid;
  }


  private getRidgePoints() {
    const ridgePoints: Vec2[] = [];
    const side = this.wsManagement.mapService.map.settings.floorSize / 2;
    for (let x = 0; x < this.elevationGrid.length; x++) {
      for (let y = 0; y < this.elevationGrid[x].length; y++) {
        const elevation = this.elevationGrid[x][y];
        const neighbors = this.getNeighborElevations(x, y);
        if (neighbors.some(z => elevation - z > RIDGE_THRESHOLD)) {
          ridgePoints.push({
            x: (x - side) / this.elevationGridResolution,
            y: (y - side) / this.elevationGridResolution,
          });
        }
      }
    }
    console.log("Ridge points count", ridgePoints.length, "Elevation grid size", this.elevationGrid.length);
    return ridgePoints;
  }

  public adjustAllObjectsElevation() {
    const sceneObjects = this.scene.children;
    for (const obj of sceneObjects) {
      const comp = (obj as any).comp;
      // if (comp instanceof PosableComponentFactory) {
      //   this.adjustObjectElevation(obj);
      //   comp.handleElevationChange(obj);
      // }
    }
  }

  public getElevationAt(loc: Vec2, defaultValue = 0): number {
    if (this.elevationGridResolution === 0) {
      return defaultValue;
    }
    const side = this.wsManagement.mapService.map.settings.floorSize / 2;
    const x = (loc.x + side) / this.elevationGridResolution;
    const y = (loc.y + side) / this.elevationGridResolution;
    const xIndex = Math.floor(x);
    const yIndex = Math.floor(y);
    if (xIndex < 0 || yIndex < 0 || xIndex >= this.elevationGrid.length || yIndex >= this.elevationGrid[0].length) {
      return defaultValue;
    }
    return this.elevationGrid[xIndex][yIndex];
  }
  public getNormalQuaternion(loc: Vec2, offset = this.elevationGridResolution): THREE.Quaternion {
    const elevation = this.getElevationAt(loc);
    const elevationX = this.getElevationAt({ x: loc.x + offset, y: loc.y });
    const elevationY = this.getElevationAt({ x: loc.x, y: loc.y + offset });

    // Compute two tangent vectors
    const tangentX = new THREE.Vector3(
      offset,
      0,
      elevationX - elevation
    );
    const tangentY = new THREE.Vector3(
      0,
      offset,
      elevationY - elevation
    );

    // Compute the normal by crossing tangentX and tangentY
    const normal = tangentX.cross(tangentY).normalize();

    // Compute the quaternion to align (0, 0, 1) with the normal
    const quaternion = new THREE.Quaternion();
    quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), normal);

    return quaternion;
  }

  public adjustObjectElevation(obj: THREE.Object3D) {
    const { x, y } = obj.position;
    obj.position.z = this.getElevationAt({ x, y }, obj.position.z);
  }

  private ridgeBoxes: THREE.Mesh[] = [];
  private createRidgeBoxes() {
    this.ridgeBoxes.forEach((box) => {
      this.scene.remove(box);
    });
    this.ridgeBoxes = [];
    const height = 20;
    for (const point of this.ridgePoints) {
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(this.elevationGridResolution, this.elevationGridResolution, height),
        new THREE.MeshBasicMaterial({ color: 0xff0000, transparent: true, opacity: 0.5 })
      );
      mesh.visible = false;
      // const elevation = this.getElevationAt(point);
      mesh.position.set(point.x, point.y, 0);
      this.scene.add(mesh);
      this.scene.scannableObjects.push(mesh);
    }
  }

  public getScan(
    origin: THREE.Vector3,
    directionZ: number,
    maxDistance: number,
    startAngle: number,
    endAngle: number,
    angleStep: number,
  ): number[] {
    const data: number[] = [];
    const points = this.ridgePoints.filter(p =>
      Math.abs(p.x - origin.x) < maxDistance && Math.abs(p.y - origin.y) < maxDistance);

    for (let angle = startAngle; angle < endAngle; angle += angleStep) {
      data.push(Number.POSITIVE_INFINITY);
    }

    for (const point of points) {
      const absAngle = Math.atan2(point.y - origin.y, point.x - origin.x);
      const relativeAngle = normalizeAngleToPi(absAngle - directionZ);
      if (relativeAngle < startAngle || relativeAngle > endAngle) {
        continue;
      }
      const relativeAngleIndex = Math.floor((relativeAngle - startAngle) / angleStep);
      const index = relativeAngleIndex;
      if (index < 0 || index >= data.length) {
        continue;
      }
      const distance =
        Math.sqrt((point.x - origin.x) ** 2 + (point.y - origin.y) ** 2)
        - this.elevationGridResolution / 2;
      if (distance < data[index]) {
        data[index] = distance;
      }
    }
    return data;
  }

  public elevationGrid: number[][] = [];
  public elevationGridResolution = 0.0;
  private ridgePoints: Vec2[] = [];
  private NO_DATA_VALUE = -10000;
  public createElevationGrid(resolution = 1.0) {
    this.elevationGridResolution = resolution;
    // elevation grid is a two-dimensional array that contains the z values of the points
    this.elevationGrid = [];
    const map = this.wsManagement.mapService.map;
    const height = 500;
    const side = map.settings.floorSize / 2;
    const raycaster = new THREE.Raycaster();
    const direction = new THREE.Vector3(0, 0, -1);
    for (let x = 0; x <= map.settings.floorSize; x += resolution) {
      const row: number[] = [];
      for (let y = 0; y <= map.settings.floorSize; y += resolution) {
        const dropPoint = new THREE.Vector3(x - side, y - side, height);
        raycaster.set(dropPoint, direction);
        const intersects = raycaster.intersectObjects(
          this.tilesRenderer!.group!.children,
          true
        );
        if (intersects.length > 0) {
          const intersection = intersects[0].point;
          row.push(intersection.z);
        }
        else {
          row.push(this.NO_DATA_VALUE);
        }
      }
      this.elevationGrid.push(row);
    }
    for (let ix = 0; ix < this.elevationGrid.length; ix++) {
      for (let iy = 0; iy < this.elevationGrid[ix].length; iy++) {
        if (this.elevationGrid[ix][iy] === this.NO_DATA_VALUE) {
          this.elevationGrid[ix][iy] = this.findClosestElevation(ix, iy);
        }
      }
    }
  }

  private findClosestElevation(ix: number, iy: number): number {
    const grid = this.elevationGrid;
    const maxDistance = 50;
    let closestElevation = 0;
    let closestDistance = Infinity;
    for (let x = Math.max(0, ix - maxDistance); x <= Math.min(grid.length - 1, ix + maxDistance); x++) {
      for (let y = Math.max(0, iy - maxDistance); y <= Math.min(grid[x].length - 1, iy + maxDistance); y++) {
        if (grid[x][y] !== this.NO_DATA_VALUE) {
          const distance = Math.sqrt((x - ix) ** 2 + (y - iy) ** 2);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestElevation = grid[x][y];
          }
        }
      }
    }
    return closestElevation;
  }

  public elevationGridVisible = false;
  private elevationGroup = new THREE.Group();
  public visualizeElevationGrid(on: boolean) {
    const elevationLines: THREE.Vector3[][] = [];
    this.scene.remove(this.elevationGroup);
    for (const child of this.elevationGroup.children) {
      const obj = child as any
      if (obj.geometry) {
        obj.geometry.dispose();
      }
    }
    if (!on) {
      this.elevationGridVisible = false;
      return;
    }
    this.elevationGroup = new THREE.Group();
    this.scene.add(this.elevationGroup);
    const points: THREE.Vector3[] = [];
    const side = this.wsManagement.mapService.map.settings.floorSize / 2;
    for (let i = 0; i < this.elevationGrid.length; i++) {
      const linePoints: THREE.Vector3[] = [];
      for (let j = 0; j < this.elevationGrid[i].length; j++) {
        const x = i * this.elevationGridResolution - side;
        const y = j * this.elevationGridResolution - side;
        const z = this.elevationGrid[i][j];
        if (z !== this.NO_DATA_VALUE) {
          const point = new THREE.Vector3(x, y, z);
          linePoints.push(point);
        }
      }
      elevationLines.push(linePoints);
    }
    for (const linePoints of elevationLines) {
      points.push(...linePoints);
    }
    for (const linePoints of elevationLines) {
      const geometry = new THREE.BufferGeometry().setFromPoints(
        linePoints
      );
      const lineMaterial = new THREE.LineBasicMaterial({ color: 0x00ff00 });
      const line = new THREE.Line(geometry, lineMaterial);
      this.elevationGroup.add(line);
    }
    const pilotLine = elevationLines[0];
    if (!pilotLine || pilotLine.length === 0) {
      console.warn("No pilot line found in elevation grid");
      return;
    }
    for (let i = 0; i < pilotLine.length; i++) {
      const pilotPoint = pilotLine[i];
      const crossLinePoints: THREE.Vector3[] = [];
      for (const linePoints of elevationLines) {
        const matchingCrossPoint = linePoints.find((point) => Math.abs(point.y - pilotPoint.y) < 0.01);
        if (matchingCrossPoint) {
          crossLinePoints.push(matchingCrossPoint);
        }
      }
      if (crossLinePoints.length > 1) {
        const geometry = new THREE.BufferGeometry().setFromPoints(
          crossLinePoints
        );
        const lineMaterial = new THREE.LineBasicMaterial({ color: 0xff0000 });
        const line = new THREE.Line(geometry, lineMaterial);
        this.elevationGroup.add(line);
      }
    }
    this.elevationGridVisible = true;
  }

  unload(): void {
    console.log("Tile unload");
  }
}
